import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

interface PageContainerProps {
  children: ReactNode;
  className?: string;
}

export function PageContainer({ children, className }: PageContainerProps) {
  return (
    <div className={cn(
      // Lyra container: clean border
      "bg-card rounded-none border border-border",
      className
    )}>
      {children}
    </div>
  );
}

interface PageHeaderProps {
  title: string;
  description?: string;
  action?: ReactNode;
}

export function PageHeader({ title, description, action }: PageHeaderProps) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
      <div>
        <h1 className="text-xl sm:text-2xl font-semibold text-foreground tracking-tight">{title}</h1>
        {description && (
          <p className="text-sm text-muted-foreground mt-1">{description}</p>
        )}
      </div>
      {action && <div className="flex-shrink-0">{action}</div>}
    </div>
  );
}

interface CardProps {
  children: ReactNode;
  className?: string;
  padding?: "none" | "sm" | "md" | "lg";
}

export function Card({ children, className, padding = "md" }: CardProps) {
  const paddingClasses = {
    none: "",
    sm: "p-3 sm:p-4",
    md: "p-4 sm:p-6",
    lg: "p-6 sm:p-8",
  };

  return (
    <div
      className={cn(
        // Lyra card: clean border
        "bg-card rounded-none border border-border",
        paddingClasses[padding],
        className
      )}
    >
      {children}
    </div>
  );
}

interface StatCardProps {
  label: string;
  value: string | number;
  color?: "default" | "success" | "warning" | "info" | "error";
}

export function StatCard({ label, value, color = "default" }: StatCardProps) {
  const colorClasses = {
    default: "border-border",
    success: "border-status-success",
    warning: "border-status-warning",
    info: "border-status-info",
    error: "border-status-error",
  };

  return (
    <div className={cn(
      // Lyra stat card: clean border, minimal
      "rounded-none border p-4 sm:p-5 bg-card",
      colorClasses[color]
    )}>
      <div className="text-xs sm:text-sm text-muted-foreground">{label}</div>
      <div className="text-xl sm:text-2xl font-semibold text-foreground mt-1">
        {value}
      </div>
    </div>
  );
}
